// src/pages/QuestionnairePage/QuestionnairePage.tsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTelegramWebApp } from '../../hooks/useTelegramWebApp';
import { useAuthStore } from '../../store/authStore';
import { useRetinolStore } from '../../store/retinolStore';
import { apiClient } from '../../services/api';
import { Button } from '../../components/ui/Button';
import { getUserTimezone } from '../../utils/helpers';

// Импорты шагов
import { Step1Age } from './steps/Step1Age';
import { Step2SkinType } from './steps/Step2SkinType';
import { Step3Sensitive } from './steps/Step3Sensitive';
import { Step3ADisease } from './steps/Step3ADisease';
import { Step4Retinol, type RetinolDurationValue, retinolDurationToWeeks } from './steps/Step4Retinol';

const Container = styled.div<{ $paddingBottom: number }>`
  /* ✅ КРИТИЧНО для iOS: используем динамический viewport вместо 100vh */
  min-height: var(--tg-viewport-stable-height, 100vh);
  padding: 20px 16px ${({ $paddingBottom }) => `${$paddingBottom}px`};
  padding-bottom: ${({ $paddingBottom }) =>
    `calc(${$paddingBottom}px + env(safe-area-inset-bottom, 0px))`};
  max-width: 430px;
  margin: 0 auto;
  background: var(--tg-theme-bg-color, #ffffff);
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: var(--tg-theme-text-color, #000000);
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: var(--tg-theme-hint-color, rgba(0,0,0,0.6));
  margin: 0;
`;

const ProgressContainer = styled.div`
  margin: 24px 0;
`;

const ProgressBar = styled.div`
  height: 6px;
  background: rgba(123, 104, 238, 0.1);
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #7B68EE, #9966CC);
  border-radius: 3px;
`;

const ProgressText = styled.div`
  text-align: center;
  font-size: 13px;
  color: var(--tg-theme-hint-color, rgba(0,0,0,0.6));
  margin-top: 8px;
`;

const StepContainer = styled(motion.div)`
  margin-bottom: 24px;
`;

const Footer = styled.div`
  position: sticky;
  bottom: 0;
  padding: 16px 0 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  background: linear-gradient(
    to top,
    var(--tg-theme-bg-color, #ffffff) 0%,
    var(--tg-theme-bg-color, #ffffff) 70%,
    transparent 100%
  );
  display: flex;
  gap: 12px;
  z-index: 10;
`;

const DEFAULT_BOTTOM_PADDING = 180;

// ===== Modals =====
const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 9999;
`;

const ModalCard = styled(motion.div)`
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.98),
    rgba(255, 255, 255, 0.95)
  );
  backdrop-filter: blur(20px);
  border: 1px solid rgba(123, 104, 238, 0.2);
  border-radius: 24px;
  padding: 32px 24px;
  max-width: 360px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(123, 104, 238, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #F59E0B, #DC2626);
  }

  [data-theme="dark"] & {
    background: linear-gradient(135deg,
      rgba(42, 42, 60, 0.98),
      rgba(32, 32, 50, 0.95)
    );
    border: 1px solid rgba(196, 181, 253, 0.2);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);

    &::before {
      background: linear-gradient(90deg, #FBBF24, #EF4444);
    }
  }
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #000;
  margin: 0 0 12px 0;
  text-align: center;

  [data-theme="dark"] & {
    color: #ffffff;
  }
`;

const ModalText = styled.p`
  font-size: 15px;
  color: rgba(0,0,0,0.7);
  line-height: 1.6;
  margin: 0 0 24px 0;
  text-align: center;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.85);
  }
`;

const ModalButton = styled(motion.button)`
  width: 100%;
  padding: 14px 20px;
  border-radius: 14px;
  border: none;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #7B68EE, #9966CC);
  color: #FFFFFF;
  box-shadow: 0 8px 24px rgba(123, 104, 238, 0.3);

  &:hover {
    transform: translateY(-2px);
  }

  [data-theme="dark"] & {
    background: linear-gradient(135deg, #C4B5FD, #D8B4FE);
    color: #000000;
    box-shadow: 0 8px 24px rgba(196, 181, 253, 0.3);
  }
`;

type TelegramWebAppWithEvents = {
  viewportHeight?: number;
  viewportStableHeight?: number;
  onEvent?: (eventType: string, callback: () => void) => void;
  offEvent?: (eventType: string, callback: () => void) => void;
};

interface QuestionnaireData {
  age: number | null;
  skinType: 'normal' | 'oily' | 'dry' | 'combination' | null;
  isSensitive: boolean | null;
  hasDisease: boolean | null;
  usesRetinol: boolean | null;
  retinolDuration: RetinolDurationValue | null;
}

export const QuestionnairePage: React.FC = () => {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegramWebApp();
  const setQuestionnaire = useRetinolStore(state => state.setQuestionnaire);
  const initializeFromBackend = useRetinolStore(state => state.initializeFromBackend);
  const setQuestionnaireCompleted = useAuthStore(state => state.setQuestionnaireCompleted);

  const[step, setStep] = useState(0);
  const[isSubmitting, setIsSubmitting] = useState(false);
  const[data, setData] = useState<QuestionnaireData>({
    age: null,
    skinType: null,
    isSensitive: null,
    hasDisease: null,
    usesRetinol: null,
    retinolDuration: null,
  });
  const[bottomPadding, setBottomPadding] = useState(DEFAULT_BOTTOM_PADDING);
  const[showAgeModal, setShowAgeModal] = useState(false);

  // ✅ стабильные ссылки (без перерендер-зависимости)
  const webAppRef = useRef<TelegramWebAppWithEvents | undefined>();
  const baseHeightRef = useRef<number>(0);

  // ✅ ИСПРАВЛЕНО: стабильный колбэк без утечек и stale-closure
  const handleViewportChange = useCallback(() => {
    if (typeof window === 'undefined') return;

    const webApp = webAppRef.current;
    const currentHeight = webApp?.viewportHeight || window.innerHeight;
    const stableHeight = webApp?.viewportStableHeight || baseHeightRef.current;

    if (typeof stableHeight === 'number' && stableHeight > 0) {
      baseHeightRef.current = stableHeight;
    }

    const keyboardOffset = Math.max(0, baseHeightRef.current - currentHeight);
    setBottomPadding(DEFAULT_BOTTOM_PADDING + keyboardOffset);
  },[]);

  // Отдельный helper для первичной инициализации/обновления
  const updatePadding = useCallback(() => {
    if (typeof window === 'undefined') return;

    const webApp = webAppRef.current;
    const currentHeight = webApp?.viewportHeight || window.innerHeight;
    const stableHeight =
      webApp?.viewportStableHeight ||
      baseHeightRef.current ||
      window.innerHeight;

    if (typeof stableHeight === 'number' && stableHeight > 0) {
      baseHeightRef.current = stableHeight;
    }

    const keyboardOffset = Math.max(0, baseHeightRef.current - currentHeight);
    setBottomPadding(DEFAULT_BOTTOM_PADDING + keyboardOffset);
  },[]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const telegram = (window as any).Telegram;
    const webApp = (telegram?.WebApp ?? undefined) as TelegramWebAppWithEvents | undefined;
    webAppRef.current = webApp;

    baseHeightRef.current =
      webApp?.viewportStableHeight ||
      webApp?.viewportHeight ||
      window.innerHeight;

    updatePadding(); // начальный вызов

    // Слушатели
    window.addEventListener('resize', handleViewportChange);

    if (webApp?.onEvent) {
      webApp.onEvent('viewportChanged', handleViewportChange);
    }

    // Очистка
    return () => {
      window.removeEventListener('resize', handleViewportChange);
      if (webAppRef.current?.offEvent) {
        webAppRef.current.offEvent('viewportChanged', handleViewportChange);
      }
    };
  },[handleViewportChange, updatePadding]);

  // Определяем эффективные шаги
  const getEffectiveSteps = () => {
    if (data.isSensitive === false) {
      return[0, 1, 2, 4]; // Без 3A
    }
    return[0, 1, 2, 3, 4]; // Все шаги
  };

  const effectiveSteps = getEffectiveSteps();
  const currentStepIndex = effectiveSteps[step];
  const totalSteps = effectiveSteps.length;
  const progress = ((step + 1) / totalSteps) * 100;

  // ✅ ИЗМЕНЕНО: Проверка возможности перехода (блокируем завершение при hasDisease === true)
  const canGoNext = () => {
    switch (currentStepIndex) {
      case 0:
        // ✅ Валидация возраста: проверяем только наличие значения (18+ проверяется в handleNext с модалом)
        return data.age !== null && data.age >= 1 && data.age <= 99;
      case 1:
        return data.skinType !== null;
      case 2:
        return data.isSensitive !== null;
      case 3:
        return data.hasDisease !== null;
      case 4:
        // Если пользователь указал заболевание — запрещаем завершение анкеты
        if (data.hasDisease === true) {
          return false;
        }
        // Иначе нужен выбор ретинола
        if (data.usesRetinol === true) {
          return data.retinolDuration !== null;
        }
        return data.usesRetinol !== null;
      default:
        return false;
    }
  };

  const handleNext = () => {
    // ✅ Проверка возраста при нажатии "Далее" на первом шаге
    if (currentStepIndex === 0 && data.age !== null && data.age < 18) {
      hapticFeedback('error');
      setShowAgeModal(true);
      return;
    }

    if (!canGoNext()) return;
    hapticFeedback('light');
    if (step < effectiveSteps.length - 1) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      hapticFeedback('light');
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    if (!canGoNext()) return;

    setIsSubmitting(true);
    hapticFeedback('success');

    try {
      // Формируем данные для бекенда
      const isSick = data.isSensitive && data.hasDisease;
      const useRetinolBefore = !isSick && data.usesRetinol === true;
      const weeksCount = useRetinolBefore ? retinolDurationToWeeks(data.retinolDuration) : null;

      const payload = {
        age: data.age || 0,
        skin_type: data.skinType || 'normal',
        is_sensitive: data.isSensitive === true,
        is_sick: isSick || false,
        use_retinol_before: useRetinolBefore,
        count_weeks_used_retinol_before: weeksCount,
        last_use_retinol: null,
        plan_start_use_retinol: null,
        need_notifications: true,
        timezone: getUserTimezone(),
      };

      if (process.env.NODE_ENV === 'development') {
        console.log('📤 Отправка анкеты:', payload);
      }

      const response = await apiClient.submitQuestionnaire(payload);

      if (response.success) {
        // Сохраняем анкету в store
        setQuestionnaire({
          age: data.age,
          skin_type: data.skinType,
          is_sensitive: data.isSensitive === true,
          is_sick: isSick || false,
          use_retinol_before: useRetinolBefore,
          last_use_retinol: null,
          plan_start_use_retinol: null,
          need_notifications: true,
          completedAt: new Date().toISOString(),
        });

        // Обработка null в recommend_products
        if (response.dates) {
          initializeFromBackend(
            response.dates,
            response.recommend_products || undefined
          );
        }

        // Обновляем статус
        setQuestionnaireCompleted(true);

        // Success → календарь
        navigate('/calendar', {
          state: { showSuccessMessage: true },
        });
      } else {
        throw new Error('Не удалось сохранить анкету');
      }
    } catch (error: any) {
      console.error('❌ Ошибка отправки анкеты:', error);
      hapticFeedback('error');

      const isSick = data.isSensitive && data.hasDisease;
      const useRetinolBefore = !isSick && data.usesRetinol === true;

      // Если ошибка сети - сохраняем локально и продолжаем
      if (error.message?.includes('fetch') || error.message?.includes('Failed')) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ Сервер недоступен, сохраняем локально');
        }

        // Сохраняем анкету в store
        setQuestionnaire({
          age: data.age,
          skin_type: data.skinType,
          is_sensitive: data.isSensitive === true,
          is_sick: isSick || false,
          use_retinol_before: useRetinolBefore,
          last_use_retinol: null,
          plan_start_use_retinol: null,
          need_notifications: true,
          completedAt: new Date().toISOString(),
        });

        // Генерируем простой календарь локально
        const today = new Date();
        const dates: string[] =[];
        for (let i = 0; i < 30; i += 3) {
          const date = new Date(today);
          date.setDate(today.getDate() + i);
          dates.push(date.toISOString());
        }
        const existingProducts = useRetinolStore.getState().recommendedProducts;
        initializeFromBackend(dates, existingProducts ||[]);

        setQuestionnaireCompleted(true);
        navigate('/calendar', { state: { showSuccessMessage: true } });
      } else {
        alert(error.message || 'Произошла ошибка. Попробуйте еще раз.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Рендер текущего шага
  const renderStep = () => {
    switch (currentStepIndex) {
      case 0:
        return <Step1Age value={data.age} onChange={(age) => setData({ ...data, age })} />;
      case 1:
        return (
          <Step2SkinType
            value={data.skinType}
            onChange={(skinType) => setData({ ...data, skinType })}
          />
        );
      case 2:
        return (
          <Step3Sensitive
            value={data.isSensitive}
            onChange={(isSensitive) => setData({ ...data, isSensitive, hasDisease: null })}
          />
        );
      case 3:
        return (
          <Step3ADisease
            value={data.hasDisease}
            onChange={(hasDisease) => setData({ ...data, hasDisease })}
          />
        );
      case 4:
        return (
          <Step4Retinol
            hasDisease={data.hasDisease || false}
            usesRetinol={data.usesRetinol}
            retinolDuration={data.retinolDuration}
            onChange={(updates) => setData({ ...data, ...updates })}
          />
        );
      default:
        return null;
    }
  };

  const isLastStep = step === effectiveSteps.length - 1;

  return (
    <Container $paddingBottom={bottomPadding}>
      <Header>
        <Title>Подберём уход под Вашу кожу</Title>
        <Subtitle>Ответьте на несколько вопросов</Subtitle>
      </Header>

      <ProgressContainer>
        <ProgressBar>
          <ProgressFill
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </ProgressBar>
        <ProgressText>
          Шаг {step + 1} из {totalSteps}
        </ProgressText>
      </ProgressContainer>

      <AnimatePresence mode="wait">
        <StepContainer
          key={currentStepIndex}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          {renderStep()}
        </StepContainer>
      </AnimatePresence>

      <Footer>
        {step > 0 && (
          <Button
            variant="outline"
            onClick={handleBack}
            style={{ flex: '0 0 auto', minWidth: '100px' }}
          >
            Назад
          </Button>
        )}

        {!isLastStep ? (
          <Button variant="primary" onClick={handleNext} disabled={!canGoNext()} fullWidth>
            Далее
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!canGoNext() || isSubmitting} // canGoNext() теперь блокирует больных
            loading={isSubmitting}
            fullWidth
          >
            {isSubmitting ? 'Сохраняем...' : 'Готово'}
          </Button>
        )}
      </Footer>

      {/* ✅ Модалка возрастного ограничения */}
      <AnimatePresence>
        {showAgeModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowAgeModal(false)}
          >
            <ModalCard
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalTitle>Возрастное ограничение</ModalTitle>
              <ModalText>
                Календарь ретинола доступен только для пользователей старше 18 лет.
                <br /><br />
                Пожалуйста, вернитесь позже.
              </ModalText>
              <ModalButton
                onClick={() => setShowAgeModal(false)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Ок
              </ModalButton>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </Container>
  );
};
